/**
 * Recipe imports: what POST /api/recipes/import returns, and the limits it keeps to.
 *
 * An import never saves anything. Its result opens in the recipe editor, where the
 * person reviews it and taps Save. It maps onto a recipe directly: name, servings,
 * notes (from the steps, see stepsToNotes), the source link, and one recipe
 * ingredient (food + grams) per matched item. Matched items are the Log sheet's
 * DraftItem, so they carry the same review flags (confidence, estimated, note).
 * Kept free of zod, like shared/recipes.ts, so the web bundle doesn't pull it in.
 */
import type { DraftItem } from './api';
import type { NutrientMap } from './nutrients';
import { MAX_ENTRY_GRAMS, RECIPE_LIMITS } from './recipes';

export const IMPORT_LIMITS = {
  /** Longest link accepted, and longest link kept: a recipe's source link. */
  url: RECIPE_LIMITS.sourceUrl,
  /** Longest pasted text, and the most page text sent to the AI. */
  text: 20_000,
  /** As many as a recipe can hold. */
  ingredients: RECIPE_LIMITS.ingredients,
  steps: 100,
  /** Characters per ingredient line. */
  ingredientLine: 250,
  /** Characters per step. */
  step: 1000,
  name: RECIPE_LIMITS.name,
  /** The most one ingredient can weigh (one log entry's limit): more than one logged food usually is, for a whole turkey or a pot of stock. */
  ingredientGrams: MAX_ENTRY_GRAMS,
} as const;

/** What an import reads: a link, pasted text, or a photo or screenshot (one of them). */
export type RecipeImportRequest =
  | { url: string }
  | { text: string }
  | { image: { mimeType: 'image/jpeg' | 'image/png' | 'image/webp'; base64: string } };

/**
 * The site's own nutrition, per serving as the site publishes it. The review
 * screen shows it next to Grams' numbers as a check. It's never saved.
 */
export interface SiteNutrition {
  perServing: NutrientMap;
  /** As written ("1 slice"), when the site says. */
  servingSize: string | null;
}

export interface ImportedIngredient {
  /** Client-side key for list rendering. */
  key: string;
  /** The ingredient as written in the source ("2 cups all-purpose flour"). */
  line: string;
  /**
   * The food it was matched to, with grams for the whole recipe. Null when it isn't
   * matched yet (AI off, or the AI couldn't place the line): the person picks the food.
   */
  item: DraftItem | null;
  /** Unmatched lines only: grams from an explicit weight in the line ("200 g", "1 lb"), to prefill the amount. */
  gramsHint: number | null;
}

/** Where the recipe was read from: the page's recipe data (JSON-LD or microdata), its text, pasted text or a photo. */
export type ImportOrigin = 'json_ld' | 'microdata' | 'page_text' | 'text' | 'image';

export interface ImportedRecipe {
  name: string;
  /** Servings the recipe makes, a whole number a recipe can have. Null when the source doesn't say: the editor asks. */
  servings: number | null;
  /** The yield as written ("Makes 12 cookies"), when there is one. */
  yieldText: string | null;
  ingredients: ImportedIngredient[];
  /** The method in order. A section heading is its own entry, ending with ":". */
  steps: string[];
  /** The page it came from, shown as "Original recipe". Null for pasted text and photos. */
  sourceUrl: string | null;
  siteNutrition: SiteNutrition | null;
  origin: ImportOrigin;
  /** True when the AI matched the ingredients to foods. */
  matchedByAI: boolean;
  /** A plain-language note for the review screen (for example why nothing is matched yet), or null. */
  notice: string | null;
}

/** Servings as a recipe takes them: a whole number within the limits, else null (the editor asks). */
export function fitServings(n: number | null): number | null {
  if (n === null || !Number.isFinite(n)) return null;
  const whole = Math.round(n);
  return Math.abs(n - whole) < 1e-9 && whole >= RECIPE_LIMITS.servings.min && whole <= RECIPE_LIMITS.servings.max ? whole : null;
}

/**
 * Steps as recipe notes: numbered, with section headings on their own lines. Whole steps
 * only, within a recipe's notes limit; `left` says how many steps didn't fit.
 */
export function stepsToNotes(steps: readonly string[]): { notes: string; left: number } {
  let n = 0;
  let notes = '';
  for (const [i, step] of steps.entries()) {
    const heading = step.endsWith(':');
    const line = heading ? `${notes ? '\n\n' : ''}${step}` : `${notes ? '\n' : ''}${n + 1}. ${step}`;
    if (notes.length + line.length > RECIPE_LIMITS.notes) return { notes, left: steps.slice(i).filter((s) => !s.endsWith(':')).length };
    notes += line;
    if (!heading) n += 1;
  }
  return { notes, left: 0 };
}
